// A run's flow grouped by week, day and variety (linked runs phase 4, spec
// docs/specs/linked-runs-bulk-sort.md §3; Thomas T2: a bulk-sort run stays
// open for an ISO week, Monday to Sunday, grouped by day and variety inside).
//
// In: the run's tips (what went in). Out: what it made: bulk-sort pallets for
// a bulk sort, pallet lines for a packing run. The day is the packhouse day
// (06:00 SAST turnover, the station's day); the week starts on its Monday.
// Pure: the page reads the rows, this only groups them.

import { stationDayAt } from '@/modules/packhouse/piecework/stationDay'

export interface FlowIn  { at: string; variety: string | null; count: number; kg: number }
export interface FlowOut { at: string; variety: string | null; count: number; kg: number }

export interface VarietyFlow { variety: string; inCount: number; inKg: number; outCount: number; outKg: number }
export interface DayFlow     { day: string; varieties: VarietyFlow[]; inKg: number; outKg: number }
export interface WeekFlow    { weekStart: string; days: DayFlow[]; inKg: number; outKg: number }

/** The Monday (YYYY-MM-DD) of the ISO week a day falls in. */
export function isoWeekStart(day: string): string {
  const d = new Date(`${day}T12:00:00Z`)
  const back = (d.getUTCDay() + 6) % 7          // Monday 0 … Sunday 6
  d.setUTCDate(d.getUTCDate() - back)
  return d.toISOString().slice(0, 10)
}

const NO_VARIETY = 'No variety'

/** Weeks newest first; days newest first; varieties by kg in, largest first. */
export function groupRunFlow(ins: FlowIn[], outs: FlowOut[], runVariety: string | null): WeekFlow[] {
  const fallback = runVariety && runVariety.trim().toUpperCase() !== 'MIXED' ? runVariety.trim() : NO_VARIETY
  const days = new Map<string, Map<string, VarietyFlow>>()
  const cell = (at: string, variety: string | null) => {
    const day = stationDayAt(Date.parse(at))
    const v = (variety ?? '').trim() || fallback
    if (!days.has(day)) days.set(day, new Map())
    const byV = days.get(day)!
    if (!byV.has(v)) byV.set(v, { variety: v, inCount: 0, inKg: 0, outCount: 0, outKg: 0 })
    return byV.get(v)!
  }
  for (const i of ins)  { const c = cell(i.at, i.variety); c.inCount  += i.count; c.inKg  += i.kg }
  for (const o of outs) { const c = cell(o.at, o.variety); c.outCount += o.count; c.outKg += o.kg }

  const weeks = new Map<string, WeekFlow>()
  for (const [day, byV] of days) {
    const varieties = [...byV.values()].sort((a, b) => b.inKg - a.inKg || a.variety.localeCompare(b.variety))
    const d: DayFlow = {
      day, varieties,
      inKg: varieties.reduce((s, v) => s + v.inKg, 0),
      outKg: varieties.reduce((s, v) => s + v.outKg, 0),
    }
    const ws = isoWeekStart(day)
    if (!weeks.has(ws)) weeks.set(ws, { weekStart: ws, days: [], inKg: 0, outKg: 0 })
    const w = weeks.get(ws)!
    w.days.push(d); w.inKg += d.inKg; w.outKg += d.outKg
  }
  const out = [...weeks.values()].sort((a, b) => b.weekStart.localeCompare(a.weekStart))
  for (const w of out) w.days.sort((a, b) => b.day.localeCompare(a.day))
  return out
}

/** Bulk-sort runs still open (tipping, or stopped and not completed) from an earlier ISO week (T2: one per week). */
export function staleBulkSortRuns<R extends { run_kind?: string | null; status?: string | null; started_at: string }>(
  runs: R[], nowMs: number,
): R[] {
  const thisWeek = isoWeekStart(stationDayAt(nowMs))
  return runs.filter(r => r.run_kind === 'BULK_SORT' && (r.status === 'ACTIVE' || r.status === 'TIPPING_COMPLETE')
    && isoWeekStart(stationDayAt(Date.parse(r.started_at))) < thisWeek)
}
